import { type MouseEvent, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { type LabelDTO, TASK_STATUS_NAMES, type TaskDTO, type TaskRefDTO, type UserDTO } from "@justone/shared";
import { errorMessage } from "../api/http";
import { useChannels, useUserMap } from "../api/queries";
import { useLabels, useLists, useRestoreTask, useTaskRef, useUpdateTask } from "../api/tasks";
import { dueTone, formatDue, PRIORITY_LABELS, PRIORITY_NAMES, recurrenceText, taskChipInfo, withTask } from "../lib/tasks";
import { Popover, useHoverIntent } from "./Popover";
import { Avatar, useToast } from "./ui";

/** 지금 화면 위에 할일 상세를 연다 (?task=) */
export function useOpenTask() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback((id: number | null) => navigate({ pathname: location.pathname, search: withTask(location.search, id) }), [navigate, location.pathname, location.search]);
}

/** 완료 체크. 반복 할일은 다음 회차로 넘어가고, 끝낸 할일은 토스트에서 되돌릴 수 있다. */
export function useToggleDone() {
  const update = useUpdateTask();
  const toast = useToast();
  return useCallback(
    (t: TaskDTO) => {
      const done = t.status === "done";
      update.mutate(
        { id: t.id, body: { status: done ? "todo" : "done" } },
        {
          onSuccess: (next) => {
            if (done) return;
            if (next.status !== "done") toast(`반복 할일: 다음 마감은 ${formatDue(next)}입니다.`);
            else toast(`"${t.title}" 완료`, { label: "되돌리기", run: () => update.mutate({ id: t.id, body: { status: t.status } }) });
          },
          onError: (e) => toast(errorMessage(e)),
        },
      );
    },
    [update, toast],
  );
}

export function TaskCheck({ task, onToggle, disabled }: { task: TaskDTO; onToggle: (t: TaskDTO) => void; disabled?: boolean }) {
  const done = task.status === "done";
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      aria-label={`${task.title} ${done ? "완료 취소" : "완료"}`}
      className={`task-check p${task.priority}${done ? " done" : ""}${task.status === "doing" ? " doing" : ""}`}
      disabled={disabled}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        onToggle(task);
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 12.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export function DueChip({ task }: { task: Pick<TaskDTO, "dueDate" | "dueTime" | "status" | "rrule" | "repeatFromCompletion"> }) {
  if (!task.dueDate) return null;
  const tone = dueTone(task);
  const repeat = recurrenceText(task);
  return (
    <span className={`due due-${tone}`} title={repeat ?? undefined}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4 10h16M9 3v4M15 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      {formatDue(task)}
      {repeat && (
        <svg viewBox="0 0 24 24" aria-label={repeat} role="img">
          <path d="M17 2l3 3-3 3M4 11V9a4 4 0 0 1 4-4h12M7 22l-3-3 3-3M20 13v2a4 4 0 0 1-4 4H4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

export function LabelChips({ ids, labels }: { ids: number[]; labels: Map<number, LabelDTO> }) {
  if (ids.length === 0) return null;
  return (
    <>
      {ids.map((id) => {
        const l = labels.get(id);
        return l ? (
          <span key={id} className={`label-chip lc-${l.color}`}>
            {l.name}
          </span>
        ) : null;
      })}
    </>
  );
}

export function PriorityFlag({ priority }: { priority: number }) {
  if (priority >= 4) return null;
  return (
    <span className={`prio prio-${priority}`} title={PRIORITY_NAMES[priority]}>
      {PRIORITY_LABELS[priority]}
    </span>
  );
}

export const useLabelMap = () => {
  const { data = [] } = useLabels();
  return new Map(data.map((l) => [l.id, l]));
};

/** 할일 한 줄: 체크, 제목, 마감·반복·장소·라벨·하위 할일·담당자 */
export function TaskRow({ task, users, showPlace = true, onOpen, depth = 0 }: { task: TaskDTO; users: Map<number, UserDTO>; showPlace?: boolean; onOpen?: (id: number) => void; depth?: number }) {
  const toggle = useToggleDone();
  const open = useOpenTask();
  const labels = useLabelMap();
  const { data: channels = [] } = useChannels();
  const { data: lists = [] } = useLists();
  const place =
    task.channelId !== null ? `#${channels.find((c) => c.id === task.channelId)?.name ?? "채널"}` : task.listId !== null ? (lists.find((l) => l.id === task.listId)?.name ?? null) : null;
  const assignee = task.assigneeId !== null ? users.get(task.assigneeId) : undefined;
  return (
    <div className={`task-row${task.status === "done" ? " is-done" : ""}`} style={depth ? { paddingLeft: 12 + depth * 24 } : undefined} data-task-id={task.id}>
      <TaskCheck task={task} onToggle={toggle} />
      <button type="button" className="task-row-main" onClick={() => (onOpen ?? open)(task.id)}>
        <span className="task-title">{task.title}</span>
        <span className="task-meta">
          <DueChip task={task} />
          <PriorityFlag priority={task.priority} />
          {task.status === "doing" && <span className="status-pill st-doing">진행중</span>}
          {task.subtaskCount > 0 && (
            <span className="subcount" title="하위 할일">
              {task.subtaskDone}/{task.subtaskCount}
            </span>
          )}
          <LabelChips ids={task.labelIds} labels={labels} />
          {showPlace && place && <span className={task.channelId !== null ? "place place-team" : "place"}>{place}</span>}
        </span>
      </button>
      {assignee && <Avatar user={assignee} size={22} />}
    </div>
  );
}

/** 지운 할일을 되살리는 토스트용 */
export function useRestore() {
  const restore = useRestoreTask();
  const toast = useToast();
  return useCallback((t: TaskDTO) => toast(`T-${t.id}을(를) 지웠습니다.`, { label: "되돌리기", run: () => restore.mutate(t.id, { onError: (e) => toast(errorMessage(e)) }) }), [restore, toast]);
}

/** 메시지 안의 `#T-123`: 번호 · 제목 · 상태 · 담당자 · 마감 (볼 수 없는 할일은 번호만). 마우스를 올리면 미리보기 */
export function TaskRefChip({ id }: { id: number }) {
  const { data } = useTaskRef(id);
  const users = useUserMap();
  const hover = useHoverIntent();
  const to = { search: `?task=${id}` };
  if (!data)
    return (
      <Link className="chip chip-task" to={to} title="할일 열기">
        T-{id}
      </Link>
    );
  const info = taskChipInfo(data, users);
  return (
    <>
      <Link
        className={`chip task-embed is-${info.status}${info.deleted ? " is-deleted" : ""}`}
        to={to}
        aria-describedby={hover.anchor ? `task-preview-${id}` : undefined}
        onPointerEnter={(e) => e.pointerType === "mouse" && hover.enter(e.currentTarget)}
        onPointerLeave={hover.leave}
        onClick={hover.close}
      >
        <span className="te-id">T-{id}</span>
        <span className="te-title">{info.deleted ? "지운 할일" : info.title}</span>
        {!info.deleted && info.statusLabel && <span className="te-status">{info.statusLabel}</span>}
        {!info.deleted && info.assignee && <span className="te-who">{info.assignee}</span>}
        {!info.deleted && info.due && (
          <span className="te-due" data-tone={info.dueTone ?? undefined}>
            {info.due}
          </span>
        )}
      </Link>
      <Popover anchor={hover.anchor} open={!!hover.anchor} onClose={hover.close} className="preview-card" role="tooltip" id={`task-preview-${id}`} onPointerEnter={hover.keep} onPointerLeave={hover.leave} passive>
        <TaskPreview task={data} users={users} onOpen={hover.close} />
      </Popover>
    </>
  );
}

/** 할일 미리보기: 상태·담당자·마감, 바로 완료 */
function TaskPreview({ task, users, onOpen }: { task: TaskRefDTO; users: Map<number, UserDTO>; onOpen: () => void }) {
  const update = useUpdateTask();
  const toast = useToast();
  const open = useOpenTask();
  const assignee = task.assigneeId !== null ? users.get(task.assigneeId) : undefined;
  const { data: channels = [] } = useChannels();
  const place = task.channelId !== null ? `#${channels.find((c) => c.id === task.channelId)?.name ?? "채널"}` : "개인 할일";
  const done = task.status === "done";
  if (task.deleted) return <p className="muted">T-{task.id}은(는) 지운 할일입니다.</p>;
  return (
    <div className="task-preview">
      <div className="tp-top">
        <span className="task-no">T-{task.id}</span>
        <span className={`status-pill st-${task.status}`}>{TASK_STATUS_NAMES[task.status]}</span>
        <PriorityFlag priority={task.priority} />
        <span className="muted">{place}</span>
      </div>
      <p className={`tp-title${done ? " is-done" : ""}`}>{task.title}</p>
      <div className="task-meta">
        {assignee ? (
          <span className="who">
            <Avatar user={assignee} size={18} />
            {assignee.displayName}
          </span>
        ) : (
          <span className="muted">담당자 없음</span>
        )}
        <DueChip task={task} />
        {task.subtaskCount > 0 && (
          <span className="subcount" title="하위 할일">
            {task.subtaskDone}/{task.subtaskCount}
          </span>
        )}
      </div>
      <div className="tp-actions">
        <button
          type="button"
          className="btn btn-sm"
          onClick={() =>
            update.mutate(
              { id: task.id, body: { status: done ? "todo" : "done" } },
              { onSuccess: (next) => !done && next.status !== "done" && toast(`반복 할일: 다음 마감은 ${formatDue(next)}입니다.`), onError: (e) => toast(errorMessage(e)) },
            )
          }
        >
          {done ? "다시 열기" : "완료"}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-quiet"
          onClick={() => {
            onOpen();
            open(task.id);
          }}
        >
          자세히
        </button>
      </div>
    </div>
  );
}
